const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const reduceAnimation = reduceMotion;

function runWhenNear(element, start, stop = () => {}) {
  if (!element) return;

  if (!('IntersectionObserver' in window)) {
    start();
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) start();
        else stop();
      }
    },
    { rootMargin: '200px 0px', threshold: 0 }
  );

  observer.observe(element);
}

function initCounters() {
  const numbers = document.querySelectorAll('.oba-stats .oba-num');
  if (numbers.length === 0 || reduceAnimation) return;

  const run = (element) => {
    const value = Number.parseInt(element.dataset.val, 10);
    const prefix = element.dataset.prefix || '';
    const suffix = element.dataset.suffix || '';
    const comma = element.dataset.format === 'comma';
    const startedAt = performance.now();
    const duration = 800;

    const tick = (now) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - Math.pow(2, -10 * progress);
      const current = Math.floor(eased * value);
      element.textContent = prefix + (comma ? current.toLocaleString('en-US') : current) + suffix;

      if (progress < 1) requestAnimationFrame(tick);
      else element.textContent = prefix + (comma ? value.toLocaleString('en-US') : value) + suffix;
    };

    requestAnimationFrame(tick);
  };

  if (!('IntersectionObserver' in window)) {
    numbers.forEach(run);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        run(entry.target);
        observer.unobserve(entry.target);
      }
    },
    { threshold: 0.1 }
  );

  numbers.forEach((number) => observer.observe(number));
}

function initRetailerReveal() {
  const row = document.querySelector('.retailer-extra');
  const originalRow = row?.closest('.section')?.querySelector('.retailer-logos');
  if (!row) return;

  const animationRows = [originalRow, row].filter(Boolean);
  const cards = [
    ...(originalRow?.querySelectorAll('a, .retailer-logos__item') || []),
    ...row.querySelectorAll('.retailer-extra__card'),
  ];
  const toggleState = (className, enabled) => {
    animationRows.forEach((animationRow) => animationRow.classList.toggle(className, enabled));
  };
  const reveal = () => {
    toggleState('retailer-extra--visible', true);
  };

  if (reduceAnimation) {
    toggleState('retailer-extra--visible', true);
    return;
  }

  cards.forEach((card, index) => card.style.setProperty('--retailer-delay', `${index * 100}ms`));
  toggleState('retailer-extra--reveal-ready', true);

  let hasEntered = false;
  let initialRevealComplete = false;
  let reentryTimer = 0;

  const enter = () => {
    if (!hasEntered) {
      hasEntered = true;
      reveal();
      window.setTimeout(() => {
        toggleState('retailer-extra--reveal-ready', false);
        initialRevealComplete = true;
      }, cards.length * 100 + 550);
      return;
    }

    if (initialRevealComplete) {
      toggleState('retailer-extra--reentering', true);
      window.clearTimeout(reentryTimer);
      reentryTimer = window.setTimeout(() => {
        toggleState('retailer-extra--reentering', false);
      }, cards.length * 100 + 650);
    }
  };

  const leave = () => {
    window.clearTimeout(reentryTimer);
    toggleState('retailer-extra--reentering', false);
  };

  if (!('IntersectionObserver' in window)) {
    enter();
    return;
  }

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry) return;
      if (entry.isIntersecting) enter();
      else leave();
    },
    { rootMargin: '100px 0px', threshold: 0.05 }
  );
  requestAnimationFrame(() => {
    requestAnimationFrame(() => observer.observe(row.closest('.section') || originalRow || row));
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) leave();
  });
}

function initMarquee({ rootSelector, trackSelector, speed, direction, startAtEnd }) {
  const root = document.querySelector(rootSelector);
  const track = root?.querySelector(trackSelector);
  if (!root || !track) return;

  const originals = Array.from(track.children);
  let distance = 0;
  let offset = 0;
  let last = 0;
  let frame = 0;
  let resizeTimer = 0;
  let initialized = false;
  let inView = false;
  let pointerPaused = false;
  let focusPaused = false;
  let pageVisible = !document.hidden;

  const isPaused = () => pointerPaused || focusPaused;

  const setup = () => {
    cancelAnimationFrame(frame);
    track.classList.remove('is-ready');
    track.querySelectorAll('[data-cg-marquee-clone]').forEach((clone) => clone.remove());
    distance = track.scrollWidth;

    originals.forEach((card) => {
      const clone = card.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-cg-marquee-clone', '');
      clone.setAttribute('inert', '');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      if (clone.matches('a, button, input, select, textarea, [tabindex]')) clone.setAttribute('tabindex', '-1');
      clone
        .querySelectorAll('a, button, input, select, textarea, [tabindex]')
        .forEach((element) => element.setAttribute('tabindex', '-1'));
      track.appendChild(clone);
    });

    if (!initialized) offset = startAtEnd ? distance : 0;
    else if (distance > 0) offset %= distance;

    initialized = true;
    track.classList.add('is-ready');
  };

  const tick = (now) => {
    if (!inView || !pageVisible || isPaused()) {
      frame = 0;
      track.classList.remove('is-animating');
      return;
    }

    const delta = Math.min(now - last, 64);
    last = now;

    if (distance > 0) {
      offset = (offset + direction * delta * speed / 1000 + distance) % distance;
      track.style.transform = `translate3d(-${offset}px,0,0)`;
    }

    frame = requestAnimationFrame(tick);
  };

  const schedule = () => {
    if (!inView || !pageVisible || isPaused() || frame !== 0) return;
    last = performance.now();
    track.classList.add('is-animating');
    frame = requestAnimationFrame(tick);
  };

  const start = () => {
    if (inView || reduceAnimation) return;
    inView = true;
    if (!initialized) setup();
    schedule();
  };

  const stop = () => {
    inView = false;
    cancelAnimationFrame(frame);
    frame = 0;
    track.classList.remove('is-animating');
  };

  if (reduceAnimation) {
    track.classList.add('is-ready');
    return;
  }

  const syncPause = () => {
    if (isPaused()) {
      cancelAnimationFrame(frame);
      frame = 0;
      track.classList.remove('is-animating');
    } else {
      schedule();
    }
  };

  root.addEventListener('mouseenter', () => {
    pointerPaused = true;
    syncPause();
  });
  root.addEventListener('mouseleave', () => {
    pointerPaused = false;
    syncPause();
  });
  root.addEventListener('focusin', () => {
    focusPaused = true;
    syncPause();
  });
  root.addEventListener('focusout', (event) => {
    focusPaused = event.relatedTarget instanceof Node && root.contains(event.relatedTarget);
    syncPause();
  });
  window.addEventListener(
    'resize',
    () => {
      if (!inView) return;
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        setup();
        frame = 0;
        schedule();
      }, 150);
    },
    { passive: true }
  );

  document.addEventListener('visibilitychange', () => {
    pageVisible = !document.hidden;
    if (pageVisible) {
      schedule();
    } else {
      cancelAnimationFrame(frame);
      frame = 0;
      track.classList.remove('is-animating');
    }
  });

  runWhenNear(root, start, stop);
}

initCounters();
initRetailerReveal();
initMarquee({
  rootSelector: '.influencers-wrap',
  trackSelector: '.inf-track',
  speed: 20,
  direction: -1,
  startAtEnd: true,
});
initMarquee({
  rootSelector: '#cgx1',
  trackSelector: '.cg-slides',
  speed: 28,
  direction: 1,
  startAtEnd: false,
});
